import { z } from 'zod';
import { EvidenceSchema, FindingStatusSchema, SeveritySchema } from '../domain/finding.js';
import { FindingClassificationSchema } from './vulnerability-intel.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, PaginationQuerySchema, UserIdSchema, listResponseSchema, } from './common.js';
/**
 * Why a finding has the status it has. Required on every decision, because "false positive"
 * with no reason is an assertion nobody can check, and the report is read by people who need
 * to check it. Ten characters is a floor against "ok", not a quality bar.
 */
export const TriageReasonSchema = z.string().trim().min(10).max(1000);
/**
 * The AI suggestion a decision was made with, if any — recorded so the trail shows how much of a
 * decision was the person's own. Filled in by the server from the stored run, never by the client.
 */
export const AiAssistSchema = z.object({
    runId: IdSchema,
    suggestedStatus: FindingStatusSchema,
    confidence: z.number().int().min(0).max(10),
    promptVersion: z.string().max(40),
    /** Whether the person chose what was suggested. */
    followed: z.boolean(),
});
/** The most recent decision, kept on the finding so a list can show it without a second read. */
export const TriageSummarySchema = z.object({
    from: FindingStatusSchema,
    to: FindingStatusSchema,
    reason: TriageReasonSchema,
    byUserId: UserIdSchema,
    byEmail: z.string().max(320).optional(),
    at: IsoDateTimeSchema,
    aiAssist: AiAssistSchema.optional(),
    /**
     * Set when Guardian recorded the decision itself, from a re-scan that proved a merged fix —
     * on behalf of `byUserId`, the person who turned automatic remediation on.
     */
    automated: z.object({ remediationId: IdSchema, verifyScanId: IdSchema }).optional(),
});
/**
 * Something a scan found.
 *
 * `fingerprint` is what makes a finding stable across runs: the same issue on the same
 * endpoint keeps its identity, so a second scan updates it rather than creating a duplicate.
 * It indexes GSI1 as `APP#{appId}` / `FIND#{fingerprint}` (§5).
 */
export const FindingSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    engagementId: IdSchema,
    applicationId: IdSchema,
    /** The scan that produced it. */
    scanId: IdSchema,
    fingerprint: z.string().min(1).max(200),
    title: z.string().min(1).max(300),
    severity: SeveritySchema,
    status: FindingStatusSchema,
    /** Scanner's own identifier, e.g. a ZAP plugin id. Kept for traceability, never parsed. */
    ruleId: z.string().max(100).optional(),
    description: z.string().max(4000).optional(),
    remediation: z.string().max(4000).optional(),
    evidence: EvidenceSchema.optional(),
    firstSeenAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
    lastTriage: TriageSummarySchema.optional(),
    /** What the scanner said it is: rule, CWE, WASC, CVEs and component. Absent on older findings. */
    classification: FindingClassificationSchema.optional(),
});
export const ListFindingsQuerySchema = PaginationQuerySchema.extend({
    severity: SeveritySchema.optional(),
    status: FindingStatusSchema.optional(),
});
export const ListFindingsResponseSchema = listResponseSchema(FindingSchema);
/**
 * PATCH /v1/engagements/{id}/findings/{findingId}
 *
 * `from` is the status the person was looking at. Two people triaging the same finding cannot
 * silently overwrite each other: the second is refused and re-reads the first's decision.
 */
export const TriageFindingRequestSchema = z.strictObject({
    from: FindingStatusSchema,
    to: FindingStatusSchema,
    reason: TriageReasonSchema,
    /**
     * The agent run whose suggestion this decision was made with. Only the id: the server reads
     * what that run actually suggested, so a client cannot attach an AI endorsement it never got.
     */
    suggestionRunId: IdSchema.optional(),
});
/**
 * One triage decision, append-only. The finding carries only the latest; these are the history,
 * and what the report lists so a reader can see how each finding came to its final status.
 */
export const TriageDecisionSchema = TriageSummarySchema.extend({
    id: IdSchema,
    orgId: OrgIdSchema,
    engagementId: IdSchema,
    findingId: IdSchema,
    fingerprint: z.string().min(1).max(200),
});
export const FindingPathSchema = z.strictObject({ id: IdSchema, findingId: IdSchema });
//# sourceMappingURL=finding.js.map